import React, { useState, useEffect, useRef } from 'react';
import { Heart, Plus, Trash2, Edit2, Upload, Eye, EyeOff, Star, Loader2, X, Calendar, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminSuccessStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    couple_names: '',
    story: '',
    wedding_date: '',
    location: '',
    image_url: '',
    is_featured: false,
    is_published: true
  });
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/extended/success-stories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStories(data.stories || []);
      }
    } catch (error) {
      console.error('Failed to load stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.couple_names || !formData.story) {
      toast({ title: "Error", description: "Please fill couple names and story", variant: "destructive" });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingStory 
        ? `${API_URL}/api/admin/extended/success-stories/${editingStory.id}`
        : `${API_URL}/api/admin/extended/success-stories`;
      
      const response = await fetch(url, {
        method: editingStory ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({ title: "Success", description: editingStory ? "Story updated" : "Story created" });
        setShowForm(false);
        setEditingStory(null);
        resetForm();
        loadStories();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save story", variant: "destructive" });
    }
  };

  const handleDelete = async (storyId) => {
    if (!window.confirm('Delete this story?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/admin/extended/success-stories/${storyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast({ title: "Deleted" });
      loadStories();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  const handleImageUpload = async (e, storyId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/extended/success-stories/${storyId}/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image_url: data.url }));
        toast({ title: "Image Uploaded" });
        loadStories();
      }
    } catch (error) {
      toast({ title: "Upload Failed", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  // New image upload for creating/editing stories (uses general upload endpoint)
  const handleNewImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: "Invalid File", description: "Please upload PNG, JPG, GIF, or WebP", variant: "destructive" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "Maximum file size is 5MB", variant: "destructive" });
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image_url: data.url }));
        toast({ title: "Photo Uploaded", description: "Image uploaded successfully" });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast({ title: "Upload Failed", description: "Could not upload image. Please try again.", variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setFormData({
      couple_names: '',
      story: '',
      wedding_date: '',
      location: '',
      image_url: '',
      is_featured: false,
      is_published: true
    });
  };

  const startEdit = (story) => {
    setEditingStory(story);
    setFormData(story);
    setShowForm(true);
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Success Stories</h1>
          <p className="text-gray-600">Manage couple testimonials and success stories</p>
        </div>
        <Button onClick={() => { resetForm(); setEditingStory(null); setShowForm(true); }} className="bg-mint-500 hover:bg-mint-600">
          <Plus className="w-4 h-4 mr-2" /> Add Story
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingStory ? 'Edit Story' : 'New Success Story'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Couple Names *</Label>
                  <Input
                    value={formData.couple_names}
                    onChange={(e) => setFormData(p => ({ ...p, couple_names: e.target.value }))}
                    placeholder="Ahmed & Fatima"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Wedding Date</Label>
                  <Input
                    type="date"
                    value={formData.wedding_date}
                    onChange={(e) => setFormData(p => ({ ...p, wedding_date: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData(p => ({ ...p, location: e.target.value }))}
                  placeholder="Toronto, Canada"
                />
              </div>

              <div className="space-y-2">
                <Label>Their Story *</Label>
                <textarea
                  value={formData.story}
                  onChange={(e) => setFormData(p => ({ ...p, story: e.target.value }))}
                  placeholder="Tell their story..."
                  className="w-full h-32 p-3 border rounded-lg resize-none"
                />
              </div>

              <div className="space-y-2">
                <Label>Photo</Label>
                <div className="space-y-2">
                  {formData.image_url && (
                    <div className="relative inline-block">
                      <img src={formData.image_url} alt="Preview" className="h-32 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => setFormData(p => ({ ...p, image_url: '' }))}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handleNewImageUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex-1"
                    >
                      {uploading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" /> {formData.image_url ? 'Change Photo' : 'Upload Photo'}</>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF, WebP (max 5MB)</p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_published}
                    onCheckedChange={(c) => setFormData(p => ({ ...p, is_published: c }))}
                  />
                  <Label>Published</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(c) => setFormData(p => ({ ...p, is_featured: c }))}
                  />
                  <Label>Featured</Label>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleSubmit} className="flex-1 bg-mint-500 hover:bg-mint-600">
                  {editingStory ? 'Update' : 'Create'} Story
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stories.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="p-8 text-center text-gray-500">
              <Heart className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No success stories yet</p>
            </CardContent>
          </Card>
        ) : (
          stories.map((story, index) => (
            <Card key={story.id || story._id || `story-${index}`} className={!story.is_published ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                {story.image_url && (
                  <img src={story.image_url} alt={story.couple_names} className="w-full h-40 object-cover rounded-lg mb-3" />
                )}
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      {story.couple_names}
                      {story.is_featured && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                    </h3>
                    {story.location && (
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {story.location}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(story)}><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(story.id || story._id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 line-clamp-3">{story.story}</p>
                <div className="flex items-center gap-2 mt-3 text-xs text-gray-400">
                  {story.is_published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  {story.is_published ? 'Published' : 'Draft'}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminSuccessStories;
